<template>
	<div class="animated fadeIn">
		<b-card>
			<b-card-title><i class="fa fa-truck"></i> Assets with Truckers Report</b-card-title>
			<b-card-sub-title>Summary of all assets distributed by truckers across supply chain</b-card-sub-title>
			<b-container fluid class="mt-4">
				<loading :active.sync="isLoading" loader="spinner" color="#20A8D8" :is-full-page="false" />

				<b-card>
					<b-row>
						<b-col md="12" sm="12" class="mt-1 mb-2">
							<b>FILTER OPTIONS</b>
						</b-col>
					</b-row>

					<!-- Date Range -->
					<b-row no-gutters>
						<b-col lg="4" md="5" sm="12" class="mr-2">
							<b-form-group label="Date From">
								<b-form-datepicker name="Date From" v-model="filterBy.dateFrom" locale="en" reset-button
									label-reset-button="Clear" :date-format-options="{
										year: 'numeric',
										month: 'short',
										day: '2-digit',
										weekday: 'short',
									}" :date-disabled-fn="dateFromDisabled" v-validate="'required'" />
								<span v-show="errors.has('Date From')" class="help-block">
									{{ errors.first('Date From') }}
								</span>
							</b-form-group>
						</b-col>
						<b-col lg="4" md="5" sm="12" class="mr-2">
							<b-form-group label="Date To">
								<b-form-datepicker name="Date To" v-model="filterBy.dateTo" locale="en" reset-button
									label-reset-button="Clear" :date-format-options="{
										year: 'numeric',
										month: 'short',
										day: '2-digit',
										weekday: 'short',
									}" :date-disabled-fn="dateFromDisabled" v-validate="'required'" />
								<span v-show="errors.has('Date To')" class="help-block">
									{{ errors.first('Date To') }}
								</span>
							</b-form-group>
						</b-col>
					</b-row>

					<!-- Company -->
					<b-row no-gutters>
						<b-col lg="4" md="10" sm="12" class="mr-2">
							<b-form-group label="Truck Company">
								<v-select name="Truck Company" class="style-chooser" label="text"
									:options="allCompanyOptions" :reduce="(company) => company.value"
									v-model="filterBy.truckCompany" v-validate="'selectRequired'">
									<template v-slot:no-options="{ search, searching }">
										<template v-if="searching">
											No results found for
											<em>
												<strong>{{ search }}</strong>
											</em>
										</template>
										<em :style="{ opacity: 0.5 }" v-else>
											Start typing to search for a company
										</em>
									</template>
								</v-select>
								<span v-show="errors.has('Truck Company')" class="help-block">
									{{ errors.first('Truck Company') }}
								</span>
							</b-form-group>
						</b-col>

						<b-col lg="4" md="10" sm="12" class="mr-2">
							<b-form-group label="Status">
								<b-form-select v-model="filterBy.status" :options="statusOptions" class="mr-2" />
							</b-form-group>
						</b-col>
					</b-row>

					<b-row no-gutters>
						<b-col sm="12">
							<b-button class="mr-1" variant="success" @click="onGenerate">
								Generate
							</b-button>
							<b-button class="mr-1" variant="primary" @click="resetFilters">
								Reset
							</b-button>
						</b-col>
					</b-row>
				</b-card>

				<div v-if="items.length > 0">
					<div class="btn-table-options">
						<b-row>
							<b-col sm="6" md="3">
								<b-dropdown text=" Select Actions " variant="dark" slot="append">
									<b-dropdown-item>
										<json-excel :data="exportData" :fields="exportFields" type="xls"
											:name="fileName + '.xls'">
											Export Report in Excel
										</json-excel>
									</b-dropdown-item>
									<b-dropdown-item>
										<json-excel :data="exportData" :fields="exportFields" type="csv"
											:name="fileName + '.csv'">
											Export Report to CSV
										</json-excel>
									</b-dropdown-item>
								</b-dropdown>
							</b-col>
							<b-col sm="6" md="3" offset-md="6" class="my-1 text-md-right">
								<b-input-group prepend="Show" append="/ Page">
									<b-form-select :options="pageOptions" v-model="perPage" />
								</b-input-group>
							</b-col>
						</b-row>
					</div>
					<b-table show-empty striped hover :items="items" :fields="fields" :current-page="currentPage"
						:per-page="perPage" :filter="filter" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc"
						:sort-direction="sortDirection" responsive>
					</b-table>
					<b-row>
						<b-col md="8" sm="12" class="my-1">
							<span class="totalDisplay">Total: {{ totalRows }}</span>
						</b-col>
						<b-col md="4" sm="12" class="my-1">
							<b-pagination align="right" :total-rows="totalRows" :per-page="perPage"
								v-model="currentPage" class="my-0" />
						</b-col>
					</b-row>
				</div>
				<div v-else>
					<b-card> No Results Found. </b-card>
				</div>
			</b-container>
		</b-card>
	</div>
</template>

<script>
// Util
import { DateUtil } from '@/utils/dateutil';
import { DropDownItemsUtil } from '@/utils/dropDownItemsUtil';

// API
import reportApi from '@/api/reportApi';

// Others
import JsonExcel from 'vue-json-excel';
import config from '@/config/env-constants';
import Loading from 'vue-loading-overlay';
import moment from 'moment';
import 'vue-loading-overlay/dist/vue-loading.css';
import _ from 'lodash';

export default {
	name: 'trucker-report',
	components: { JsonExcel, Loading },
	data() {
		return {
			items: [],
			fields: [
				{
					key: 'truckCompany',
					sortable: true,
				},
				{
					key: 'plateNo',
					sortable: true,
				},
				{
					key: 'driver',
					sortable: true,
				},
				{
					key: 'assetType',
					sortable: true,
				},
				{
					key: 'deployedAssets',
					label: 'Deployed',
					sortable: true,
				},
				{
					key: 'receivedAssets',
					label: 'Received',
					sortable: true,
				},
				{
					key: 'dispatchId',
					sortable: true,
				},
				{
					key: 'status',
					sortable: false,
				},
				{
					key: 'source',
					sortable: false,
				},
				{
					key: 'destination',
					sortable: false,
				},
				{
					key: 'dateDeployed',
					sortable: true,
				},
				{
					key: 'dateReceived',
					sortable: true,
				},
			],
			currentPage: 1,
			perPage: 10,
			totalRows: 0,
			pageOptions: [5, 10, 15, 25, 50, 100],
			sortBy: null,
			sortDesc: false,
			sortDirection: 'asc',
			filter: null,

			defaultFilterBy: {
				dateFrom: moment().format('YYYY-MM-DD'),
				dateTo: moment().format('YYYY-MM-DD'),
				status: null,
				truckCompany: config.companyDefaultValue,
			},
			filterBy: {
				dateFrom: moment().format('YYYY-MM-DD'),
				dateTo: moment().format('YYYY-MM-DD'),
				status: null,
				truckCompany: config.companyDefaultValue,
			},
			prevFilterBy: null,

			allCompanyOptions: [],
			statusOptions: config.dispatchStatus,

			allCompaniesObj: {},

			isSuperAdmin: this.$store.getters.isSuperAdmin,
			loggedUser: this.$store.getters.loggedUser,
			loggedUserCompany: this.$store.getters.loggedUserCompany,
			// Check for loader
			isLoading: false,
		};
	},
	computed: {
		/**
		 * Returns the set of data to be included in the export. For now this just
		 * returns the data as is.
		 *
		 * @returns {Array} the set of data to be included in the export.
		 */
		exportData() {
			return this.items;
		},

		/**
		 * Derives the field information based from the data table configuration.
		 *
		 * @returns {object} the fields to be included in the export.
		 */
		exportFields() {
			let fields = {};

			_.forEach(this.fields, (field) => {
				fields[field.key] = field.key;
			});

			return fields;
		},

		fileName() {
			let filename = 'AssetsWithTrucker';

			if (this.filterBy.truckCompany.id) {
				filename += `_${this.filterBy.truckCompany.name}`;
			}

			let currTimeStamp = DateUtil.getCurrentTimestamp();
			return `${filename}_${DateUtil.getDateInMMDDYYYYFormat(currTimeStamp)}`;
		},
	},
	mounted() {
		setTimeout(async () => {
			try {
				// Don't initiate data retrieval when the account is not authenticated
				if (!this.$store.getters.isAuthenticated) {
					return;
				}

				// show loading indicator
				this.isLoading = true;

				this.allCompaniesObj = this.$store.getters.companies;
				this.allCompanyOptions = DropDownItemsUtil.retrieveCompanyItems(
					this.allCompaniesObj, this.loggedUserCompany
				);
				this.resetFilters();
			} catch (error) {
				this.$toaster.error(
					'Error loading data. Please reload the page again.'
				);
			}

			// hide loading indicator
			this.isLoading = false;
		}, config.timeout);
	},
	methods: {
		resetFilters() {
			this.items = [];
			this.filterBy = Object.assign({}, this.defaultFilterBy);

			// reset validation
			this.$validator.reset();
			this.errors.clear();
		},

		dateFromDisabled(_ymd, date) {
			return date > new Date();
		},

		async validateFilter() {
			let isValid = await this.$validator.validateAll();

			if (!isValid) {
				this.$toaster.warning('Please address the field/s with invalid input.');
				return isValid;
			}

			if (this.filterBy.dateFrom > this.filterBy.dateTo) {
				this.$toaster.warning(
					'Invalid Date Range. Date From must be less than Date To.'
				);
				isValid = false;
			} else if (
				DateUtil.getNoOfDays(this.filterBy.dateFrom, this.filterBy.dateTo) > 90
			) {
				this.$toaster.warning(
					'Invalid Date Range. Data range is allowed up to 90 days difference.'
				);
				isValid = false;
			}

			return isValid;
		},

		getReportParameters() {
			let filter = Object.assign({}, this.filterBy);
			filter.companyId = this.loggedUserCompany.id;

			if (this.filterBy.dateFrom) {
				filter.fromTimestamp = DateUtil.startDateTimeStamp(
					new Date(this.filterBy.dateFrom)
				);
			}
			if (this.filterBy.dateTo) {
				filter.toTimestamp = DateUtil.endDateTimeStamp(
					new Date(this.filterBy.dateTo)
				);
			}

			return filter;
		},
		async onGenerate() {
			let isValid = await this.validateFilter();
			if (!isValid) {
				return;
			}

			// Reset items
			this.items = [];
			// Show loader
			this.isLoading = true;

			try {
				let view = this.isSuperAdmin ? config.view.ADMIN : config.view.COMPANY;
				let reportParam = this.getReportParameters();

				let { data } = await reportApi.getAssetsWithTruckersReport(
					reportParam,
					view,
					this.loggedUser.id
				);

				if (data.isSuccess) {
					let items = data.items;

					if (_.isEmpty(items)) {
						this.$toaster.warning('No Results found.');
					} else {
						this.items = items;
						this.totalRows = this.items.length;
					}

					// Hide loader
					this.isLoading = false;
					// Mark the prevFilter
					this.prevFilter = Object.assign({}, this.filterBy);
				} else {
					this.$toaster.error(
						'Error loading data. Please reload the page again.'
					);
					// hide loading indicator
					this.isLoading = false;
				}
			} catch (error) {
				this.$toaster.error(
					'Error loading data. Please reload the page again.'
				);

				// hide loading indicator
				this.isLoading = false;
			}
		},
	},
};
</script>

<style scoped>

</style>
